import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Remove, CheckCircle } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import type { Recipe } from '../../types';
import { formatMoney } from '../../components/commercial/contact';

const EXPIRY_MONTHS = [6, 12, 18, 24];

const todayLotCode = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `L${String(d.getFullYear()).slice(2)}${p(d.getMonth() + 1)}${p(d.getDate())}`;
};

const addMonths = (months: number) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

/**
 * Producir un lote en un paso: crea la orden de producción, la inicia (reserva materias
 * primas) y la completa. El sistema calcula el costo real por unidad del lote.
 */
const MobileProducePage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [qty, setQty] = useState(Number(params.get('qty')) || 10);
  const [lotCode, setLotCode] = useState(todayLotCode());
  const [expiryMonths, setExpiryMonths] = useState<number | null>(12);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ product: string; qty: number; lot: string; unitCost?: number } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getRecipes({ page: 1, pageSize: 100 });
        const list = res.recipes || [];
        setRecipes(list);
        const pid = Number(params.get('productId'));
        const pre = pid ? list.find(r => r.productId === pid) : list.length === 1 ? list[0] : undefined;
        if (pre) setRecipe(pre);
      } catch {
        setRecipes([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [params]);

  const expirationDate = useMemo(() => (expiryMonths ? addMonths(expiryMonths) : undefined), [expiryMonths]);

  const submit = async () => {
    if (!recipe) { setError('Elegí qué producir'); return; }
    if (qty <= 0) { setError('Cantidad inválida'); return; }
    setSaving(true);
    setError(null);
    let orderId: number | null = null;
    try {
      const order = await api.createProductionOrder({
        productId: recipe.productId,
        recipeId: recipe.id,
        quantity: qty,
        scheduledDate: new Date().toISOString(),
        lotCode: lotCode || undefined,
        expirationDate,
        notes: 'Cargada desde el celular',
      });
      orderId = order.id;
      await api.startProductionOrder(order.id);
      await api.completeProductionOrder(order.id, { lotCode: lotCode || undefined, expirationDate, actualQuantity: qty });
      let unitCost: number | undefined;
      try {
        // Al completar, el costo del producto queda en el costo real de este lote.
        const product: any = await api.getProduct(recipe.productId);
        unitCost = product?.cost;
      } catch { /* el costo se ve igual en Rentabilidad por lote */ }
      setDone({ product: recipe.productName || recipe.name, qty, lot: lotCode, unitCost });
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'No se pudo cargar la producción';
      setError(orderId
        ? `${msg}. La orden quedó creada en Producción para terminarla desde la versión completa.`
        : msg);
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <Stack spacing={2} alignItems="center" sx={{ pt: 4, textAlign: 'center' }}>
        <CheckCircle color="success" sx={{ fontSize: 80 }} />
        <Typography variant="h5" fontWeight={800}>Lote cargado</Typography>
        <Typography variant="h6">{done.qty} × {done.product}</Typography>
        <Typography color="text.secondary">Lote {done.lot}</Typography>
        {done.unitCost != null && (
          <Typography variant="h6">Costo por unidad: <b>{formatMoney(done.unitCost)}</b></Typography>
        )}
        <Button variant="contained" size="large" sx={{ minHeight: 56, width: '100%' }} onClick={() => { setDone(null); setLotCode(todayLotCode()); }}>
          Otro lote
        </Button>
        <Button variant="outlined" size="large" sx={{ minHeight: 56, width: '100%' }} onClick={() => navigate('/m')}>Volver al inicio</Button>
      </Stack>
    );
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  if (recipes.length === 0) {
    return <Alert severity="info">Todavía no hay formulaciones cargadas. Cargá la receta del producto desde la versión completa (Catálogo → Formulaciones).</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={700}>¿Qué producís?</Typography>
      <Stack spacing={1}>
        {recipes.map(r => (
          <Card key={r.id} variant="outlined" sx={{ borderColor: recipe?.id === r.id ? 'primary.main' : undefined, borderWidth: recipe?.id === r.id ? 2 : 1 }}>
            <CardActionArea onClick={() => setRecipe(r)}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle1" fontWeight={700}>{r.productName || r.name}</Typography>
                {r.productName && r.name !== r.productName && <Typography variant="body2" color="text.secondary">{r.name}</Typography>}
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>

      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Cantidad producida</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => setQty(q => Math.max(1, q - 1))} sx={{ width: 56, height: 56, border: 1, borderColor: 'divider' }} aria-label="Menos"><Remove /></IconButton>
          <TextField
            type="number"
            value={qty}
            onChange={e => setQty(Number(e.target.value) || 0)}
            inputProps={{ inputMode: 'numeric', style: { textAlign: 'center', fontSize: 28, fontWeight: 800 } }}
            sx={{ flex: 1 }}
          />
          <IconButton onClick={() => setQty(q => q + 1)} sx={{ width: 56, height: 56, border: 1, borderColor: 'divider' }} aria-label="Más"><Add /></IconButton>
        </Stack>
      </Box>

      <TextField label="Código de lote" value={lotCode} onChange={e => setLotCode(e.target.value)} InputProps={{ sx: { fontSize: 18, minHeight: 56 } }} />

      <Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Vence en</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <Chip label="Sin vencimiento" onClick={() => setExpiryMonths(null)} color={expiryMonths == null ? 'primary' : 'default'}
            variant={expiryMonths == null ? 'filled' : 'outlined'} sx={{ fontSize: 15, height: 40 }} />
          {EXPIRY_MONTHS.map(m => (
            <Chip key={m} label={`${m} meses`} onClick={() => setExpiryMonths(m)} color={expiryMonths === m ? 'primary' : 'default'}
              variant={expiryMonths === m ? 'filled' : 'outlined'} sx={{ fontSize: 15, height: 40 }} />
          ))}
        </Stack>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      <Button variant="contained" color="warning" size="large" onClick={submit} disabled={saving || !recipe}
        sx={{ minHeight: 60, fontSize: 20, fontWeight: 800 }}>
        {saving ? 'Cargando…' : `Producir ${qty} unidades`}
      </Button>
      <Typography variant="caption" color="text.secondary">
        Descuenta las materias primas de la receta y crea el lote con su costo real por unidad.
      </Typography>
    </Stack>
  );
};

export default MobileProducePage;
